import React from 'react';
import clsx from 'clsx';
import {type UseFormReturn} from 'react-hook-form';
import {faCloudUpload} from '@fortawesome/pro-solid-svg-icons';
import {
	type ExtendReactHookFormDialogProps,
	ReactHookFormDialog,
} from '@netx/core-components/FormDialog';
import {
	ScrollBorder,
	scrollBorderClasses,
} from '@netx/core-components/ScrollBorder';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {type SingleFileUploadFormValues} from '../../hooks/useSingleFileUploadForm';
import {UploadActionLabel, UploadSingleFileTitle} from '../../locales';
import {uploadDropzoneClasses} from '../UploadDropzone/uploadDropzoneClasses';
import {UploadSingleFile, type UploadSingleFileProps} from './UploadSingleFile';
import {
	getUploadSingleFileDialogUtilityClass,
	UPLOAD_SINGLE_FILE_DIALOG_NAME,
	type UploadSingleFileDialogClasses,
} from './uploadSingleFileDialogClasses';

type UploadSingleFileDialogOwnerState =
	WithClasses<UploadSingleFileDialogClasses>;

const useUtilityClasses = (state: UploadSingleFileDialogOwnerState) => {
	const {classes} = state;
	const slots = {
		root: ['root'],
		form: ['form'],
	};
	return composeClasses(slots, getUploadSingleFileDialogUtilityClass, classes);
};

const UploadSingleFileDialogRoot = styled(ReactHookFormDialog, {
	name: UPLOAD_SINGLE_FILE_DIALOG_NAME,
	slot: 'Root',
})(({theme}) => ({}));

const UploadSingleFileStyled = styled(UploadSingleFile, {
	name: UPLOAD_SINGLE_FILE_DIALOG_NAME,
	slot: 'Form',
})(({theme}) => ({
	padding: theme.spacing(0, 2, 0, 2),
}));

const UploadSingleFileDialogContent = styled(ScrollBorder)(({theme}) => ({
	[theme.breakpoints.down('sm')]: {
		[`& .${scrollBorderClasses.scrollWrapper}, & .${scrollBorderClasses.scrollWrapperContent}, & .${scrollBorderClasses.scrollWrapperInner}`]:
			{
				flex: '1 1 auto',
			},
	},
}));

export interface UploadSingleFileDialogProps<
	TFieldValues extends SingleFileUploadFormValues = SingleFileUploadFormValues,
> extends WithClasses<UploadSingleFileDialogClasses>,
		ExtendReactHookFormDialogProps,
		Omit<UploadSingleFileProps, 'classes'> {
	form: UseFormReturn<TFieldValues>;
}

export const UploadSingleFileDialogPrimative = <
	TFieldValues extends SingleFileUploadFormValues = SingleFileUploadFormValues,
>({
	classes: _classes,
	className,
	error,
	form,
	open,
	FormProps,
	DialogProps,
	onClose,
	onSubmit,
	...props
}: UploadSingleFileDialogProps<TFieldValues>) => {
	const ownerState: UploadSingleFileDialogOwnerState = {
		classes: _classes,
		className,
	};
	const classes = useUtilityClasses(ownerState);

	const isSubmitting = form.formState.isSubmitting;

	return (
		<UploadSingleFileDialogRoot
			className={clsx(classes.root, className)}
			confirmLabel={UploadActionLabel.SUBMIT}
			disableFormPadding
			open={open}
			title={UploadSingleFileTitle.UPLOAD}
			{...FormProps}
			disableSubmit={
				FormProps?.disableSubmit ||
				(props.uploadStarted && !props.uploadComplete)
			}
			error={form.formState.errors.root?.message ?? error}
			form={form}
			isDirty={form.formState.isDirty || !!props.file}
			onClose={onClose}
			onSubmit={onSubmit}
			DialogProps={{
				closeDisabled:
					isSubmitting || (props.uploadStarted && !props.uploadComplete),
				icon: faCloudUpload,
				disableContentPadding: true,
				fullScreenWhenMobile: true,
				fullWidth: true,
				maxWidth: 'sm',
				preventBackdropClose: true,
				...DialogProps,
			}}
		>
			<UploadSingleFileDialogContent>
				<UploadSingleFileStyled
					className={classes.form}
					error={error}
					{...props}
				/>
			</UploadSingleFileDialogContent>
		</UploadSingleFileDialogRoot>
	);
};

export const UploadSingleFileDialog = UploadSingleFileDialogPrimative;

export default UploadSingleFileDialog;
